import React from 'react'
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import Indicator from '../condition/indicators/types/Indicator'
import { COMPOSITE_LIMITS, CompositeExpr, CompositeOp } from '../../../types/composite'
import { DraftStep } from './types'
import StepRefSelect from './StepRefSelect'

const OP_LABELS: Record<CompositeOp, string> = {
  '+': '+',
  '-': '−',
  '*': '×',
  '/': '÷',
  min: 'min',
  max: 'max',
  abs: 'abs',
  neg: 'negate',
  '>': '> (1/0)',
  '<': '< (1/0)',
  '>=': '≥ (1/0)',
  '<=': '≤ (1/0)',
  and: 'and (1/0)',
  or: 'or (1/0)',
  not: 'not (1/0)',
}

const isUnary = (op: CompositeOp) => op === 'abs' || op === 'neg' || op === 'not'
const isVariadic = (op: CompositeOp) => op === 'min' || op === 'max' || op === 'and' || op === 'or'

const ZERO: CompositeExpr = { t: 'const', value: 0 }

interface ExprEditorProps {
  expr: CompositeExpr
  onChange: (expr: CompositeExpr) => void
  steps: DraftStep[]
  beforeIndex: number
  getIndicatorByValue: (value: string) => Indicator | undefined
  depth?: number
}

const ExprEditor: React.FC<ExprEditorProps> = ({
  expr,
  onChange,
  steps,
  beforeIndex,
  getIndicatorByValue,
  depth = 1,
}) => {
  const canNest = depth < COMPOSITE_LIMITS.maxExprDepth

  const handleKindChange = (kind: string) => {
    if (kind === 'const') onChange(ZERO)
    else if (kind === 'ref') onChange({ t: 'ref', ref: { source: 'close' } })
    else if (kind === 'op') onChange({ t: 'op', op: '+', args: [ZERO, ZERO] })
  }

  const handleOpChange = (op: CompositeOp) => {
    if (expr.t !== 'op') return
    let args = expr.args
    if (isUnary(op)) args = args.slice(0, 1)
    else while (args.length < 2) args = [...args, ZERO]
    if (!isVariadic(op) && !isUnary(op)) args = args.slice(0, 2)
    onChange({ t: 'op', op, args })
  }

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Operand</InputLabel>
          <Select label="Operand" value={expr.t} onChange={(e) => handleKindChange(e.target.value)}>
            <MenuItem value="const">Number</MenuItem>
            <MenuItem value="ref">Data</MenuItem>
            <MenuItem value="op" disabled={!canNest}>
              Operation
            </MenuItem>
          </Select>
        </FormControl>

        {expr.t === 'const' && (
          <TextField
            size="small"
            type="number"
            label="Value"
            value={expr.value}
            onChange={(e) => onChange({ t: 'const', value: parseFloat(e.target.value) || 0 })}
            sx={{ width: 120 }}
          />
        )}

        {expr.t === 'ref' && (
          <StepRefSelect
            steps={steps}
            beforeIndex={beforeIndex}
            value={expr.ref}
            onChange={(ref) => ref && onChange({ t: 'ref', ref })}
            getIndicatorByValue={getIndicatorByValue}
            label="Data"
          />
        )}

        {expr.t === 'op' && (
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Operation</InputLabel>
            <Select
              label="Operation"
              value={expr.op}
              onChange={(e) => handleOpChange(e.target.value as CompositeOp)}
            >
              {(Object.keys(OP_LABELS) as CompositeOp[]).map((op) => (
                <MenuItem key={op} value={op}>
                  {OP_LABELS[op]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {expr.t === 'op' && (
        <Box
          display="flex"
          flexDirection="column"
          gap={1}
          sx={{
            borderLeft: '2px solid',
            borderColor: 'background.custom4',
            pl: 1.5,
            ml: 0.5,
          }}
        >
          {expr.args.map((arg, argIndex) => (
            <Box key={argIndex} display="flex" gap={1} alignItems="flex-start">
              <Box flexGrow={1}>
                <ExprEditor
                  expr={arg}
                  onChange={(next) =>
                    onChange({
                      t: 'op',
                      op: expr.op,
                      args: expr.args.map((a, i) => (i === argIndex ? next : a)),
                    })
                  }
                  steps={steps}
                  beforeIndex={beforeIndex}
                  getIndicatorByValue={getIndicatorByValue}
                  depth={depth + 1}
                />
              </Box>
              {isVariadic(expr.op) && expr.args.length > 2 && (
                <Tooltip title="Remove operand">
                  <IconButton
                    size="small"
                    onClick={() =>
                      onChange({
                        t: 'op',
                        op: expr.op,
                        args: expr.args.filter((_, i) => i !== argIndex),
                      })
                    }
                  >
                    <RemoveCircleOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}
          {isVariadic(expr.op) && (
            <Box>
              <Tooltip title="Add operand">
                <IconButton
                  size="small"
                  onClick={() => onChange({ t: 'op', op: expr.op, args: [...expr.args, ZERO] })}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}

export default ExprEditor
